/* Block Animations — Vanilla JS for Shopify Liquid Theme */
(function () {
  'use strict';

  // ── Constants ──────────────────────────────────────────────
  const LIGHT_SECTIONS = new Set([
    '02-problem', '04-meet-block', '05-how-it-works',
    '06-what-changes', '09-product-detail', '12-faq', 'volume-upsell',
    // block-comparison.liquid's own background moved off the outer
    // [data-screen-label] element and onto .block-comparison-pin
    // (nested inside it) when the scroll-pin mechanic was added - the
    // luminance fallback below only ever samples the outer section's
    // own background-color, so it no longer sees any color there and
    // silently falls back to "dark". Listed explicitly here instead of
    // trying to make the fallback reach into a nested/sometimes-fixed
    // descendant.
    '11-comparison',
  ]);

  // Shared by setupNav() (checks at the nav's own y) and setupFloatingCta()
  // (checks near the bottom of the viewport) — is the section sitting behind
  // viewport-y `y` a light-background section?
  function isLightSectionAt(y) {
    let light = false;
    document.querySelectorAll('[data-screen-label]').forEach(section => {
      const r = section.getBoundingClientRect();
      if (r.top <= y && r.bottom > y) {
        const label = section.dataset.screenLabel || '';
        if (LIGHT_SECTIONS.has(label)) {
          light = true;
        } else {
          // Luminance fallback
          const bg = getComputedStyle(section).backgroundColor;
          const m  = bg.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
          if (m && (+m[1] * 0.299 + +m[2] * 0.587 + +m[3] * 0.114) > 127) light = true;
        }
      }
    });
    return light;
  }

  // ── Klaviyo client-side subscribe/track ─────────────────────
  // Shared by setupFloatingCtaForm() and setupFooterNewsletter() -
  // both post straight to Klaviyo's own public Client API instead of
  // Shopify's /contact form, which never actually reached Klaviyo (a
  // Shopify customer got created, but nothing subscribed in Klaviyo -
  // discovered 2026-08-25). COMPANY_ID is Klaviyo's public "Site ID"
  // (safe to ship client-side, this endpoint is designed for exactly
  // this) - LIST_ID is Block's single "Email List", used by both
  // forms on purpose (agreed 2026-08-25) with double opt-in turned off
  // for that list in Klaviyo itself, so this subscribe call takes
  // effect immediately rather than waiting on a confirmation click.
  const KLAVIYO_COMPANY_ID = 'XwPuru';
  const KLAVIYO_LIST_ID = 'XC3YPe';
  const KLAVIYO_REVISION = '2024-10-15';

  function klaviyoSubscribe(email) {
    return fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${KLAVIYO_COMPANY_ID}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', revision: KLAVIYO_REVISION },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            profile: { data: { type: 'profile', attributes: { email } } },
          },
          relationships: {
            list: { data: { type: 'list', id: KLAVIYO_LIST_ID } },
          },
        },
      }),
    });
  }

  // Fired only by the floating CTA (see 'Requested 10% Code' below),
  // not the plain footer newsletter signup - both land on the same
  // list, but only the CTA path should trigger the discount-code flow
  // in Klaviyo, so that flow is set to trigger on this event rather
  // than on "subscribed to list" in general.
  function klaviyoTrackEvent(email, metricName) {
    return fetch(`https://a.klaviyo.com/client/events/?company_id=${KLAVIYO_COMPANY_ID}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', revision: KLAVIYO_REVISION },
      body: JSON.stringify({
        data: {
          type: 'event',
          attributes: {
            properties: {},
            metric: { data: { type: 'metric', attributes: { name: metricName } } },
            profile: { data: { type: 'profile', attributes: { email } } },
          },
        },
      }),
    });
  }

  // ── Entry point ────────────────────────────────────────────
  function init() {
    document.body.classList.add('js-animate');
    setupScrollReveal();
    setupBtnFill();
    setupFloatingCta();
    setupNav();
    setupHero();
    setupTrustbarAutoScroll();
    setupSloganKinetic();
    setupFAQ();
    setupVolumeUpsell();
    setupLeadMagnet();
    setupFooterNewsletter();
    setupSmoothScrollLinks();
    setupProductPage();
    setupCartDrawer();
    setupStoryCards();
    setupOutcomesScroll();
    setupOutcomesMedia();
    setupJourneyScroll();
    setupComparisonScroll();
    setupComparisonTooltips();
    setupComparisonSwipeHint();
    setupUseCasesGrid();
    setupPromoBar();
    setupDragScroll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // ── 1. Scroll Reveal ───────────────────────────────────────
  function setupScrollReveal() {
    function revealEl(el) { el.classList.add('visible'); }

    function onScroll() {
      document.querySelectorAll('.reveal:not(.visible), .clip-reveal:not(.visible)').forEach(el => {
        const r = el.getBoundingClientRect();
        if (r.top < window.innerHeight * 0.93 && r.bottom > 0) revealEl(el);
      });
    }

    const obs = new IntersectionObserver(
      (entries, o) => entries.forEach(e => {
        if (e.isIntersecting) { revealEl(e.target); o.unobserve(e.target); }
      }),
      { threshold: 0.01, rootMargin: '0px' }
    );
    document.querySelectorAll('.reveal, .clip-reveal').forEach(el => obs.observe(el));

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll(); // immediate pass for above-fold elements
  }

  // ── 1b. Button radial fill ─────────────────────────────────
  // Tracks pointer position over .block-btn-primary/.block-btn-ghost so the
  // ::before circle (see block-design.css) reveals from wherever the cursor
  // entered, instead of a fixed center point. One delegated listener covers
  // every button on the page — no per-button setup needed.
  function setupBtnFill() {
    document.addEventListener('pointermove', e => {
      const btn = e.target.closest('.block-btn-primary, .block-btn-ghost, .block-floating-cta__trigger, .block-floating-cta__submit, .block-footer-newsletter-submit, .block-article-back');
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      btn.style.setProperty('--btn-x', `${e.clientX - rect.left}px`);
      btn.style.setProperty('--btn-y', `${e.clientY - rect.top}px`);
    }, { passive: true });
  }

  // ── block-use-cases-grid.liquid hover mark ─────────────────
  // Same "reveal from wherever the cursor entered" idea as setupBtnFill
  // above (--mark-x/--mark-y, read by .block-usecases-card__mark in
  // block-design.css), but set once per pointerenter rather than
  // continuously on every pointermove - a logo mark should land at the
  // entry point and hold still there for that hover, not chase the
  // cursor around the card. Also rolls a fresh random rotation each
  // entry (--mark-rotate), per-card listeners rather than the button
  // pattern's single delegated one since there are only 8 of these,
  // not an open-ended set of buttons scattered across every template.
  function setupUseCasesGrid() {
    const cards = document.querySelectorAll('[data-usecases-card]');
    cards.forEach(card => {
      card.addEventListener('pointerenter', e => {
        const rect = card.getBoundingClientRect();
        card.style.setProperty('--mark-x', `${e.clientX - rect.left}px`);
        card.style.setProperty('--mark-y', `${e.clientY - rect.top}px`);
        card.style.setProperty('--mark-rotate', `${Math.round(Math.random() * 360)}deg`);
      });
    });
  }

  // ── 1c. Floating CTA ───────────────────────────────────────
  // Shows once the 2nd [data-screen-label] section (e.g. the homepage's
  // "02-problem", right after the hero) reaches the top of the viewport —
  // never during the hero itself. Skipped entirely on templates that don't
  // have a 2nd labeled section (nothing to trigger off of).
  //
  // Clicking the collapsed pill grows it in place (JS-measured height, see
  // expand() below — the pure-CSS grid/max-height tricks used elsewhere in
  // this file don't fit here because the collapsed and expanded states are
  // both fixed, known sizes, not "0 to auto") into the newsletter form, then
  // reuses the same validate/submit logic as setupLeadMagnet() below —
  // duplicated rather than shared because the two forms live in unrelated
  // DOM subtrees with their own data-fcta-*/data-lead-* attribute scopes.
  function setupFloatingCta() {
    const el = document.getElementById('block-floating-cta');
    const trigger = document.getElementById('block-floating-cta-trigger');
    const closeBtn = document.getElementById('block-floating-cta-close');
    const panelInner = el && el.querySelector('.block-floating-cta__panel-inner');
    if (!el || !trigger || !panelInner) return;

    const wasForcedVisible = el.classList.contains('is-visible'); // design mode

    // CSS transitions can't interpolate from `width: auto`, so lock the
    // collapsed pill's shrink-to-fit width in as an explicit px value up
    // front — visually identical to auto, but now the width transition
    // (auto → the .is-expanded target) actually has two real numbers to
    // animate between instead of snapping.
    const collapsedWidth = trigger.offsetWidth;
    el.style.width = collapsedWidth + 'px';

    function expand() {
      if (el.classList.contains('is-expanded')) return;
      el.style.height = panelInner.offsetHeight + 'px';
      el.style.width = ''; // let the .is-expanded class rule's width win (higher specificity than an empty inline value)
      el.classList.add('is-expanded');
      trigger.setAttribute('aria-expanded', 'true');
      setTimeout(() => {
        const input = el.querySelector('[data-fcta-input]');
        if (input) input.focus({ preventScroll: true });
      }, 200);
    }

    function collapse() {
      if (!el.classList.contains('is-expanded')) return;
      el.style.height = '54px';
      el.style.width = collapsedWidth + 'px';
      el.classList.remove('is-expanded');
      trigger.setAttribute('aria-expanded', 'false');
    }

    trigger.addEventListener('click', expand);
    if (closeBtn) closeBtn.addEventListener('click', collapse);

    document.addEventListener('click', e => {
      if (el.classList.contains('is-expanded') && !el.contains(e.target)) collapse();
    });
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && el.classList.contains('is-expanded')) collapse();
    });

    setupFloatingCtaForm(el);

    if (wasForcedVisible) return; // scroll-triggered visibility skipped in design mode

    const screenSection = document.querySelectorAll('[data-screen-label]')[1];
    if (!screenSection) return;

    // Two rAFs before trusting the first scroll read — scroll-position
    // restoration or late-loading hero media can otherwise flip this true
    // then instantly false on load, flashing the pill for a frame.
    let ready = false;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => { ready = true; onScroll(); });
    });

    function onScroll() {
      if (!ready) return;
      el.classList.toggle('is-visible', screenSection.getBoundingClientRect().top <= 0);
      // Same light/dark detection as the nav (isLightSectionAt), checked at
      // the pill's own vertical center near the bottom of the viewport
      // instead of the nav's y=64 near the top.
      el.classList.toggle('is-light', isLightSectionAt(window.innerHeight - 30));
    }

    window.addEventListener('scroll', onScroll, { passive: true });
  }

  // Newsletter form inside the floating CTA's expanded panel — same Shopify
  // customer/newsletter submit mechanism as setupLeadMagnet(), duplicated
  // (not shared) since it's a separate DOM subtree with its own data-fcta-*
  // attributes and strings script.
  function setupFloatingCtaForm(root) {
    const form      = root.querySelector('[data-fcta-form]');
    const input     = root.querySelector('[data-fcta-input]');
    const submitBtn = root.querySelector('[data-fcta-submit]');
    const errorEl   = root.querySelector('[data-fcta-error]');
    const successEl = root.querySelector('[data-fcta-success]');
    if (!form || !input) return;

    const stringsEl = document.getElementById('block-floating-cta-strings');
    const strings    = stringsEl ? JSON.parse(stringsEl.textContent) : {};

    const isValidEmail = v => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v.trim());

    function triggerShake() {
      form.classList.remove('shake');
      void form.offsetWidth; // force reflow
      form.classList.add('shake');
      form.addEventListener('animationend', () => form.classList.remove('shake'), { once: true });
    }

    function showError(msg) {
      if (!errorEl) return;
      errorEl.textContent = msg;
      errorEl.hidden = false;
      input.classList.add('error');
    }

    function clearError() {
      if (!errorEl) return;
      errorEl.textContent = '';
      errorEl.hidden = true;
      input.classList.remove('error');
    }

    const originalSubmitLabel = submitBtn ? submitBtn.textContent : '';

    function submit(e) {
      e && e.preventDefault();
      clearError();

      if (!input.value.trim()) {
        showError(strings.emailRequired);
        triggerShake();
        return;
      }
      if (!isValidEmail(input.value)) {
        showError(strings.emailInvalid);
        triggerShake();
        return;
      }

      const email = input.value.trim();
      if (submitBtn) { submitBtn.textContent = strings.sendingLabel; submitBtn.disabled = true; }
      input.disabled = true;

      klaviyoSubscribe(email)
        .then(res => {
          if (!res.ok) throw new Error('Klaviyo subscribe failed');
          // Distinct event from a plain footer signup - lets the
          // discount-code flow in Klaviyo trigger only for people who
          // came through this form (see klaviyoTrackEvent's own
          // comment above). A failure here shouldn't block the
          // success state - the subscribe itself already succeeded.
          return klaviyoTrackEvent(email, 'Requested 10% Code').catch(() => {});
        })
        .then(done)
        .catch(fail);

      function done() {
        form.hidden = true;
        if (successEl) successEl.hidden = false;
        const root = form.closest('#block-floating-cta');
        if (root) root.style.height = root.querySelector('.block-floating-cta__panel-inner').offsetHeight + 'px';
      }

      function fail() {
        if (submitBtn) { submitBtn.textContent = originalSubmitLabel; submitBtn.disabled = false; }
        input.disabled = false;
        showError(strings.submitError);
        triggerShake();
      }
    }

    form.addEventListener('submit', submit);
    input.addEventListener('input', clearError);
  }

  // ── 2. Navigation ──────────────────────────────────────────
  function setupNav() {
    const announcement = document.getElementById('block-announcement');
    const wrapper      = document.getElementById('block-nav-wrapper');
    const nav          = document.getElementById('block-nav');
    const hamburger    = document.getElementById('block-nav-hamburger');
    const mobileMenu   = document.getElementById('block-mobile-menu');
    const menuClose    = document.getElementById('block-mobile-menu-close');

    if (!nav) return;

    let scrolled  = false;
    let menuOpen  = false;

    // ── Scroll: glass + bar collapse ──
    function onScroll() {
      const isScrolled = window.scrollY > 40;
      if (isScrolled !== scrolled) {
        scrolled = isScrolled;
        wrapper && wrapper.classList.toggle('block-scrolled', scrolled);
        announcement && announcement.classList.toggle('block-scrolled', scrolled);
        nav.classList.toggle('block-glass', scrolled);
      }

      // navDark detection — which section is behind the nav (at y=64)?
      nav.classList.toggle('block-nav-light', isLightSectionAt(64));
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    // ── Mobile menu ──
    function openMenu() {
      menuOpen = true;
      mobileMenu && mobileMenu.classList.add('open');
      mobileMenu && mobileMenu.removeAttribute('aria-hidden');
      hamburger && hamburger.classList.add('open');
      hamburger && hamburger.setAttribute('aria-expanded', 'true');
      hamburger && hamburger.setAttribute('aria-label', hamburger.dataset.closeLabel || 'Close menu');
      document.body.style.overflow = 'hidden';
      // Focus first link
      setTimeout(() => {
        const first = mobileMenu && mobileMenu.querySelector('a, button');
        first && first.focus();
      }, 40);
    }

    function closeMenu() {
      menuOpen = false;
      mobileMenu && mobileMenu.classList.remove('open');
      mobileMenu && mobileMenu.setAttribute('aria-hidden', 'true');
      hamburger && hamburger.classList.remove('open');
      hamburger && hamburger.setAttribute('aria-expanded', 'false');
      hamburger && hamburger.setAttribute('aria-label', hamburger.dataset.openLabel || 'Open menu');
      document.body.style.overflow = '';
      hamburger && hamburger.focus();
    }

    hamburger && hamburger.addEventListener('click', () => {
      menuOpen ? closeMenu() : openMenu();
    });
    menuClose && menuClose.addEventListener('click', closeMenu);

    // Close on Escape
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && menuOpen) closeMenu();
    });

    // Close on mobile nav link click
    mobileMenu && mobileMenu.querySelectorAll('a').forEach(link => {
      link.addEventListener('click', () => {
        closeMenu();
      });
    });
  }

  // ── 3. Hero: Parallax + PixelPulseOverlay ─────────────────
  function setupHero() {
    const parallaxEl = document.querySelector('[data-parallax]');
    const canvas     = document.querySelector('[data-pixel-canvas]');

    // Scroll parallax
    if (parallaxEl) {
      function onScroll() {
        parallaxEl.style.transform = `translateY(${window.scrollY * 0.3}px)`;
      }
      window.addEventListener('scroll', onScroll, { passive: true });
    }

    // Mouse tilt
    const heroSection = document.querySelector('[data-screen-label="01-hero"]');
    const heroImg     = heroSection && heroSection.querySelector('[data-hero-img]');
    if (heroSection && heroImg) {
      heroSection.addEventListener('mousemove', e => {
        const r = heroSection.getBoundingClientRect();
        const x = (e.clientX - r.left - r.width  / 2) / r.width;
        const y = (e.clientY - r.top  - r.height / 2) / r.height;
        heroImg.style.transform = `scale(1.04) translate(${x * -22}px, ${y * -16}px)`;
      });
      heroSection.addEventListener('mouseleave', () => {
        heroImg.style.transform = 'scale(1.04) translate(0, 0)';
      });
    }

    // Trust bar dividers double as the old scroll indicator — click
    // either one to smooth-scroll to whichever section actually follows
    // the hero (not a fixed anchor, so this stays correct if the
    // homepage order changes). Same offset/scrollTo pattern as
    // setupSmoothScrollLinks() below — no scrollIntoView().
    const nextSectionButtons = document.querySelectorAll('[data-scroll-next-section]');
    if (nextSectionButtons.length && heroSection) {
      const heroWrapper = heroSection.closest('.shopify-section') || heroSection;
      const nextSection = heroWrapper.nextElementSibling;
      if (nextSection) {
        nextSectionButtons.forEach(btn => {
          btn.addEventListener('click', () => {
            const top = nextSection.getBoundingClientRect().top + window.scrollY - 64;
            window.scrollTo({ top, behavior: 'smooth' });
          });
        });
      }
    }

    // PixelPulseOverlay canvas
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const S   = 24; // block size
    let cols, rows, blocks, frame;
    let t = 0, scrollOff = 0;

    function initCanvas() {
      canvas.width  = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
      cols   = Math.ceil(canvas.width  / S);
      rows   = Math.ceil(canvas.height / S);
      blocks = Array.from({ length: cols * rows }, () => ({
        phase:      Math.random() * Math.PI * 2,
        speed:      0.25 + Math.random() * 0.6,
        maxOpacity: Math.random() < 0.064 ? (0.032 + Math.random() * 0.112) : 0,
      }));
    }

    function onCanvasScroll() { scrollOff = window.scrollY * 0.25; }
    window.addEventListener('scroll', onCanvasScroll, { passive: true });

    function draw() {
      t += 0.012;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const b = blocks[r * cols + c];
          if (!b.maxOpacity) continue;
          const op = b.maxOpacity * (0.5 + 0.5 * Math.sin(t * b.speed + b.phase)) * (1 - r / rows);
          ctx.fillStyle = `rgba(255,255,255,${op.toFixed(3)})`;
          ctx.fillRect(c * S, r * S + scrollOff, S - 1, S - 1);
        }
      }
      frame = requestAnimationFrame(draw);
    }

    initCanvas();
    draw();

    const ro = new ResizeObserver(initCanvas);
    ro.observe(canvas);
  }

  // Mobile hero trust bar: slow automatic back-and-forth scroll through
  // the 3 USPs (right to left, then reverses at the last one back to
  // the first, and so on) - on top of, not instead of, the existing
  // manual swipe. scroll-snap-type is switched off while the loop drives
  // scrollLeft itself (native snap correcting mid-animation would make
  // it stutter) and switched back on the moment the user actually
  // touches/drags, so their own swipe still snaps normally; it's handed
  // back to the auto-scroll loop after a few seconds of inactivity.
  function setupTrustbarAutoScroll() {
    const track = document.querySelector('.block-hero-trustbar');
    if (!track) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const mobileMq    = window.matchMedia('(max-width: 768px)');
    const SPEED        = 22;   // px/second - calm, matches the promo marquee's unhurried pace
    const RESUME_DELAY = 2500; // ms of inactivity before auto-scroll takes back over

    let direction   = 1; // 1 = toward the last USP, -1 = back toward the first
    let lastT       = null;
    let paused      = false;
    let resumeTimer = null;

    function maxScroll() {
      return track.scrollWidth - track.clientWidth;
    }

    function step(t) {
      if (lastT === null) lastT = t;
      const dt = (t - lastT) / 1000;
      lastT = t;

      if (!paused && mobileMq.matches) {
        const max = maxScroll();
        if (max > 1) {
          let next = track.scrollLeft + direction * SPEED * dt;
          if (next >= max) { next = max; direction = -1; }
          else if (next <= 0) { next = 0; direction = 1; }
          track.scrollLeft = next;
        }
      }
      requestAnimationFrame(step);
    }

    function pause() {
      paused = true;
      track.style.scrollSnapType = 'x mandatory';
      clearTimeout(resumeTimer);
      resumeTimer = setTimeout(() => {
        paused = false;
        lastT = null;
        track.style.scrollSnapType = 'none';
      }, RESUME_DELAY);
    }

    track.style.scrollSnapType = 'none';
    track.addEventListener('pointerdown', pause, { passive: true });
    track.addEventListener('touchstart', pause, { passive: true });
    track.addEventListener('wheel', pause, { passive: true });

    requestAnimationFrame(step);
  }

  // ── 4. Slogan Kinetic Typography ──────────────────────────
  function setupSloganKinetic() {
    const section = document.querySelector('[data-kinetic-section]');
    if (!section) return;

    // FitLine: auto-scale each line to fill container width
    const fitLineEls = section.querySelectorAll('[data-fit-line]');
    fitLineEls.forEach(el => {
      function fit() {
        el.style.fontSize = '100px';
        const ratio = el.parentElement.offsetWidth / el.scrollWidth;
        el.style.fontSize = Math.floor(100 * ratio) + 'px';
      }
      fit();
      document.fonts && document.fonts.ready.then(fit);
      window.addEventListener('resize', fit, { passive: true });
    });

    // Phase machine
    const pair1Lines = section.querySelectorAll('[data-kinetic-pair="1"]');
    const pair2Lines = section.querySelectorAll('[data-kinetic-pair="2"]');
    let prev1 = false, prev2 = false;
    let timer1 = null, timer2 = null;

    function setPhase(lines, phase) {
      lines.forEach(el => el.setAttribute('data-kinetic-phase', phase));
    }

    function activate(lines, timerSlot) {
      clearTimeout(timerSlot.ref);
      setPhase(lines, 'idle');
      timerSlot.ref = setTimeout(() => setPhase(lines, 'active'), 32);
    }

    function deactivate(lines, timerSlot) {
      clearTimeout(timerSlot.ref);
      setPhase(lines, 'exiting');
      timerSlot.ref = setTimeout(() => setPhase(lines, 'idle'), 880);
    }

    const t1 = { ref: null };
    const t2 = { ref: null };

    function update() {
      const vH   = window.innerHeight;
      const rect  = section.getBoundingClientRect();
      const p     = Math.max(0, Math.min(1, (vH - rect.top) / (section.offsetHeight + vH)));
      const w1    = p >= 0.05 && p < 0.65;
      const w2    = p >= 0.40 && p < 0.75;

      if (w1 !== prev1) { prev1 = w1; w1 ? activate(pair1Lines, t1) : deactivate(pair1Lines, t1); }
      if (w2 !== prev2) { prev2 = w2; w2 ? activate(pair2Lines, t2) : deactivate(pair2Lines, t2); }
    }

    window.addEventListener('scroll', update, { passive: true });
    update();
  }

  // ── 5. FAQ Accordion ───────────────────────────────────────
  function setupFAQ() {
    const items = document.querySelectorAll('[data-faq-item]');
    if (!items.length) return;

    items.forEach(item => {
      const trigger = item.querySelector('[data-faq-trigger]');
      const body    = item.querySelector('[data-faq-body]');
      if (!trigger || !body) return;

      trigger.addEventListener('click', () => {
        const isOpen = body.classList.contains('open');

        // Close all items
        items.forEach(it => {
          const b = it.querySelector('[data-faq-body]');
          const t = it.querySelector('[data-faq-trigger]');
          const icon = t && t.querySelector('svg');
          if (b) b.classList.remove('open');
          if (t) t.setAttribute('aria-expanded', 'false');
          if (icon) icon.style.transform = 'none';
        });

        // Toggle clicked
        if (!isOpen) {
          body.classList.add('open');
          trigger.setAttribute('aria-expanded', 'true');
          const icon = trigger.querySelector('svg');
          if (icon) icon.style.transform = 'rotate(180deg)';
        }
      });
    });
  }

  // ── 6. Volume Upsell ───────────────────────────────────────
  function setupVolumeUpsell() {
    const cards = document.querySelectorAll('[data-volume-option]');
    const cta   = document.querySelector('[data-volume-cta]');
    if (!cards.length) return;

    cards.forEach(card => {
      card.addEventListener('click', () => {
        cards.forEach(c => {
          c.removeAttribute('data-selected');
          c.setAttribute('aria-checked', 'false');
        });
        card.setAttribute('data-selected', '');
        card.setAttribute('aria-checked', 'true');

        if (cta) {
          cta.textContent = card.dataset.ctaLabel || 'Order now';
          if (card.dataset.ctaHref) cta.href = card.dataset.ctaHref;
        }
      });

      // Keyboard support
      card.addEventListener('keydown', e => {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          card.click();
        }
      });
    });
  }

  // ── 7. Lead Magnet ─────────────────────────────────────────
  function setupLeadMagnet() {
    const form      = document.querySelector('[data-lead-form]');
    const input     = document.querySelector('[data-lead-input]');
    const submitBtn = document.querySelector('[data-lead-submit]');
    const errorEl   = document.querySelector('[data-lead-error]');
    const successEl = document.querySelector('[data-lead-success]');
    if (!form || !input) return;

    const stringsEl = document.getElementById('block-lead-magnet-strings');
    const strings    = stringsEl ? JSON.parse(stringsEl.textContent) : {};

    const isValidEmail = v => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v.trim());

    function triggerShake() {
      form.classList.remove('shake');
      // Force reflow
      void form.offsetWidth;
      form.classList.add('shake');
      form.addEventListener('animationend', () => form.classList.remove('shake'), { once: true });
    }

    function showError(msg) {
      if (!errorEl) return;
      errorEl.textContent = msg;
      errorEl.hidden = false;
      input.classList.add('error');
    }

    function clearError() {
      if (!errorEl) return;
      errorEl.textContent = '';
      errorEl.hidden = true;
      input.classList.remove('error');
    }

    function submit(e) {
      e && e.preventDefault();
      clearError();

      if (!input.value.trim()) {
        showError(strings.emailRequired);
        triggerShake();
        return;
      }
      if (!isValidEmail(input.value)) {
        showError(strings.emailInvalid);
        triggerShake();
        return;
      }

      if (submitBtn) submitBtn.textContent = strings.sendingLabel;

      // Submit to Shopify customer newsletter
      const data = new FormData();
      data.append('form_type', 'customer');
      data.append('utf8', '✓');
      data.append('contact[email]', input.value.trim());
      data.append('contact[tags]', 'newsletter');

      fetch('/contact#contact_form', {
        method: 'POST',
        body: data,
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      })
        .then(() => {
          form.hidden = true;
          if (successEl) successEl.hidden = false;
        })
        .catch(() => {
          // Graceful fallback — still show success to user
          form.hidden = true;
          if (successEl) successEl.hidden = false;
        });
    }

    form.addEventListener('submit', submit);
    input.addEventListener('input', clearError);
  }

  // ── 7b. Footer newsletter ──────────────────────────────────
  // Same Shopify customer/newsletter submit mechanism as setupLeadMagnet(),
  // duplicated rather than shared since it's a separate DOM subtree with
  // its own data-footer-*/strings script (block-footer.liquid is a static
  // section, so it's always present exactly once per page).
  function setupFooterNewsletter() {
    const form      = document.querySelector('[data-footer-form]');
    const input     = document.querySelector('[data-footer-input]');
    const errorEl   = document.querySelector('[data-footer-error]');
    const successEl = document.querySelector('[data-footer-success]');
    if (!form || !input) return;

    const stringsEl = document.getElementById('block-footer-newsletter-strings');
    const strings    = stringsEl ? JSON.parse(stringsEl.textContent) : {};

    const isValidEmail = v => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v.trim());

    function showError(msg) {
      if (!errorEl) return;
      errorEl.textContent = msg;
      errorEl.hidden = false;
      input.classList.add('error');
    }

    function clearError() {
      if (!errorEl) return;
      errorEl.textContent = '';
      errorEl.hidden = true;
      input.classList.remove('error');
    }

    const submitBtn = document.querySelector('[data-footer-submit]');
    const originalSubmitLabel = submitBtn ? submitBtn.textContent : '';

    function submit(e) {
      e && e.preventDefault();
      clearError();

      if (!input.value.trim()) { showError(strings.emailRequired); return; }
      if (!isValidEmail(input.value)) { showError(strings.emailInvalid); return; }

      const email = input.value.trim();
      if (submitBtn) submitBtn.disabled = true;
      input.disabled = true;

      klaviyoSubscribe(email)
        .then(res => {
          if (!res.ok) throw new Error('Klaviyo subscribe failed');
        })
        .then(done)
        .catch(fail);

      function done() {
        form.hidden = true;
        if (successEl) successEl.style.display = 'block';
      }

      function fail() {
        if (submitBtn) submitBtn.disabled = false;
        input.disabled = false;
        showError(strings.submitError);
      }
    }

    form.addEventListener('submit', submit);
    input.addEventListener('input', clearError);
  }

  // ── 8. Smooth scroll anchor links ─────────────────────────
  function setupSmoothScrollLinks() {

    document.addEventListener('click', e => {
      const link = e.target.closest('[data-smooth-scroll]');
      if (!link) return;
      const href = link.getAttribute('href') || link.dataset.target;
      if (!href || !href.startsWith('#')) return;
      const target = document.getElementById(href.slice(1));
      if (!target) return;
      e.preventDefault();
      const top = target.getBoundingClientRect().top + window.scrollY - 64;
      window.scrollTo({ top, behavior: 'smooth' });
    });
  }

  // ── Announcement bar promo carousel ───────────────────────
  // Only one [data-promo-item] is ever un-hidden at a time, so the
  // group's rendered width always matches whichever message is showing
  // (see .block-announcement__group in block-design.css) - the chevrons
  // naturally sit right against it via flex+gap, no width math needed.
  function setupPromoBar() {
    const viewport = document.querySelector('[data-promo-viewport]');
    if (!viewport) return;
    const items = viewport.querySelectorAll('[data-promo-item]');
    const count = items.length;
    if (count < 2) return;

    let index = 0;
    let timer = null;

    // Runs on both breakpoints: desktop's own min(56vw,460px) cap on
    // .block-announcement__message (block-design.css) already grows the
    // chevrons to fit shorter/longer messages up to the
    // language-switcher-safe limit; mobile's cap is just 100% of
    // whatever's left between the edge-pinned chevrons. Either way,
    // measures the *unscrolled* overflow against whichever cap is
    // currently in effect and drives it via a CSS custom property
    // instead of ellipsis-truncating, so the full sentence stays
    // reachable by scroll however wide the container currently is.
    function updateScroll(item) {
      const inner = item.querySelector('[data-promo-text]');
      if (!inner) return;
      // .is-scrolling adds 16px of left/right padding on the inner text
      // (block-design.css) - add the class FIRST, so scrollWidth is
      // measured against that already-padded width. Measuring before
      // adding it (the previous bug) undercounted the overflow by
      // exactly that padding, so the animation never quite scrolled far
      // enough left to bring the padded end fully into view.
      item.classList.add('is-scrolling');
      const overflow = inner.scrollWidth - item.clientWidth;
      if (overflow > 4) {
        item.style.setProperty('--promo-scroll-distance', `-${overflow}px`);
      } else {
        item.classList.remove('is-scrolling');
        item.style.removeProperty('--promo-scroll-distance');
      }
    }

    // Vertical ticker: outgoing message gets .is-leaving (slides up and
    // out) at the same moment the incoming one gets .is-active (slides
    // up into place from below) - see the transition on
    // .block-announcement__message in block-design.css. .is-leaving is
    // cleared after the transition so that message parks back below,
    // ready to re-enter next time it's due.
    function show(i) {
      const nextIndex = (i + count) % count;
      if (nextIndex === index) return;
      const current = items[index];
      const next = items[nextIndex];

      current.classList.remove('is-active');
      current.classList.add('is-leaving');
      current.setAttribute('aria-hidden', 'true');

      next.classList.add('is-active');
      next.setAttribute('aria-hidden', 'false');
      updateScroll(next);

      window.setTimeout(() => current.classList.remove('is-leaving'), 500);

      index = nextIndex;
    }

    // recursive setTimeout, not setInterval - the delay before advancing
    // depends on whether the CURRENT item is mid-marquee (in which case
    // autoplay must wait for the right-to-left scroll to actually finish
    // revealing the sentence, ~9.5s - matching the 56% mark of the 17s
    // promo-scroll keyframe in block-design.css, i.e. once it's paused at
    // the fully-scrolled-left end - rather than always firing at a flat
    // 5s and cutting a still-scrolling message off mid-way).
    function scheduleNext() {
      clearTimeout(timer);
      const active = items[index];
      const delay = active.classList.contains('is-scrolling') ? 9500 : 5000;
      timer = setTimeout(() => {
        show(index + 1);
        scheduleNext();
      }, delay);
    }

    // Manual clicks always act immediately (this pacing is only about
    // autoplay not cutting a marquee off), then reschedule from the
    // newly active item's own state.
    function goTo(i) {
      show(i);
      scheduleNext();
    }

    const prevBtn = document.querySelector('[data-promo-prev]');
    const nextBtn = document.querySelector('[data-promo-next]');
    if (prevBtn) prevBtn.addEventListener('click', () => goTo(index - 1));
    if (nextBtn) nextBtn.addEventListener('click', () => goTo(index + 1));

    // Re-measure on resize (viewport width changes what overflows, and
    // can cross the mobile breakpoint entirely) and orientation change.
    window.addEventListener('resize', () => updateScroll(items[index]), { passive: true });

    updateScroll(items[index]);
    scheduleNext();
  }

  // ── 9. Product page ────────────────────────────────────────
  function setupProductPage() {
    const galleryMain = document.querySelector('[data-gallery-main]');
    if (!galleryMain) return;

    // Gallery — slide track (real drag-follow on mobile), thumbnails,
    // click zones, and a native custom-cursor chevron on desktop.
    const galleryTrack = document.querySelector('[data-gallery-track]');
    const slides        = galleryTrack ? Array.from(galleryTrack.querySelectorAll('[data-gallery-slide]')) : [];
    const thumbs         = document.querySelectorAll('[data-gallery-thumb]');
    const prevBtn        = document.querySelector('[data-gallery-prev]');
    const nextBtn        = document.querySelector('[data-gallery-next]');

    let activeIndex = 0;

    function setTrackTransform(px, animate) {
      if (!galleryTrack) return;
      galleryTrack.style.transition = animate ? 'transform 0.4s var(--ease-spring)' : 'none';
      galleryTrack.style.transform = `translate3d(${px}px, 0, 0)`;
    }

    function goTo(index, animate) {
      if (animate === undefined) animate = true;
      if (!slides.length) return;
      activeIndex = (index + slides.length) % slides.length;
      thumbs.forEach(t => t.removeAttribute('data-active'));
      if (thumbs[activeIndex]) thumbs[activeIndex].setAttribute('data-active', '');
      setTrackTransform(-activeIndex * galleryMain.getBoundingClientRect().width, animate);
    }

    thumbs.forEach((thumb, i) => {
      thumb.addEventListener('click', () => goTo(i));
    });

    if (prevBtn) prevBtn.addEventListener('click', () => goTo(activeIndex - 1));
    if (nextBtn) nextBtn.addEventListener('click', () => goTo(activeIndex + 1));

    window.addEventListener('resize', () => goTo(activeIndex, false));

    // Desktop — the native cursor itself becomes the glass-circle chevron
    // (a data-URI SVG set via the CSS `cursor` property), not a JS-positioned
    // DOM element trailing the pointer — that's what removes the lag/"stuck
    // behind the real cursor" feel. Same technique as enterphase.app/products/phase.
    //
    // Colors/proportions are a literal copy of .block-btn-ghost__icon (the
    // "See how it works" circle in the homepage hero, block-design.css §10):
    // same --glass-bg/--glass-border opacities, same chevron path/stroke-width
    // as used everywhere else in Block's icon system, translated (not scaled)
    // into a wider canvas so the chevron keeps that button's loose padding
    // inside the circle instead of nearly filling it.
    function buildGalleryCursor(pathD) {
      const svg = "<svg xmlns='http://www.w3.org/2000/svg' width='44' height='44' viewBox='0 0 42 42'>" +
        "<circle cx='21' cy='21' r='20' fill='white' fill-opacity='0.07' stroke='white' stroke-opacity='0.10' stroke-width='1'/>" +
        `<g transform='translate(9,9)'><path d='${pathD}' stroke='white' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round' fill='none'/></g></svg>`;
      return `url("data:image/svg+xml,${encodeURIComponent(svg)}") 22 22, pointer`;
    }
    const GALLERY_CURSOR_PREV = buildGalleryCursor('M19 12H5M12 5l-7 7 7 7');
    const GALLERY_CURSOR_NEXT = buildGalleryCursor('M5 12h14M12 5l7 7-7 7');
    const hoverCapable = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

    if (hoverCapable) {
      galleryMain.addEventListener('mousemove', event => {
        const rect = galleryMain.getBoundingClientRect();
        const x = event.clientX - rect.left;
        galleryMain.style.cursor = x < rect.width / 2 ? GALLERY_CURSOR_PREV : GALLERY_CURSOR_NEXT;
      });
      galleryMain.addEventListener('mouseleave', () => {
        galleryMain.style.cursor = '';
      });
    }

    // Mobile — drag-follow swipe: the track visibly slides with the
    // finger and snaps to the nearest slide on release (not a jump-cut).
    if (slides.length > 1) {
      let isDragging  = false;
      let touchStartX = 0;
      let dragDeltaPx = 0;
      let mainWidth   = 0;

      galleryMain.addEventListener('touchstart', event => {
        isDragging  = true;
        touchStartX = event.touches[0].clientX;
        dragDeltaPx = 0;
        mainWidth   = galleryMain.getBoundingClientRect().width;
      }, { passive: true });

      galleryMain.addEventListener('touchmove', event => {
        if (!isDragging) return;
        dragDeltaPx = event.touches[0].clientX - touchStartX;
        setTrackTransform(-activeIndex * mainWidth + dragDeltaPx, false);
      }, { passive: true });

      galleryMain.addEventListener('touchend', () => {
        if (!isDragging) return;
        isDragging = false;
        const SWIPE_THRESHOLD = mainWidth * 0.18;
        if (dragDeltaPx < -SWIPE_THRESHOLD) goTo(activeIndex + 1);
        else if (dragDeltaPx > SWIPE_THRESHOLD) goTo(activeIndex - 1);
        else goTo(activeIndex);
      });
    }

    goTo(0, false);

    // Variant swatches
    const variantIdInput   = document.getElementById('variant-id-input');
    const variantNameLabel = document.getElementById('variant-name-label');
    const swatches         = document.querySelectorAll('[data-variant-swatch]');

    swatches.forEach(swatch => {
      swatch.addEventListener('click', () => {
        swatches.forEach(s => {
          s.removeAttribute('data-active');
          s.style.opacity = '0.4';
          s.style.outlineOffset = '0px';
        });
        swatch.setAttribute('data-active', '');
        swatch.style.opacity = '1';
        swatch.style.outlineOffset = '2px';

        if (variantNameLabel) variantNameLabel.textContent = swatch.dataset.variantName;
        syncBundleCTA();

        if (swatch.dataset.variantImage) {
          const firstSlideImg = slides[0] ? slides[0].querySelector('img') : null;
          if (firstSlideImg) {
            firstSlideImg.src = swatch.dataset.variantImage;
            goTo(0, false);
          }
        }
      });
    });

    // Bundle selector — the selected card decides WHICH product goes in
    // the cart (its data-bundle-variants map, keyed by colour: the 2-pack
    // is its own product with its own variant ids) and how many; the
    // active colour swatch picks the entry. Also keeps the price line
    // under the title in step with the selected card.
    const bundleCards    = document.querySelectorAll('[data-bundle-card]');
    const quantityInput  = document.getElementById('quantity-hidden');
    const productCta     = document.getElementById('product-cta');
    const stickyCta      = document.getElementById('sticky-cta');
    const priceCompare   = document.querySelector('[data-price-compare]');
    const priceCurrent   = document.querySelector('[data-price-current]');
    const priceSave      = document.querySelector('[data-price-save]');
    const lowStock       = document.querySelector('[data-low-stock]');

    function syncBundleCTA() {
      const selected = document.querySelector('[data-bundle-card][data-selected]');
      if (!selected) return;
      const label = selected.dataset.bundleLabel || '';
      if (productCta) productCta.textContent = label;
      if (stickyCta)  stickyCta.textContent  = label;
      if (quantityInput) quantityInput.value = selected.dataset.bundleQty || '1';

      let variants = {};
      try { variants = JSON.parse(selected.dataset.bundleVariants || '{}'); } catch (_) {}
      const activeSwatch = document.querySelector('[data-variant-swatch][data-active]');
      const colour = activeSwatch ? activeSwatch.dataset.variantName : null;
      const variantId = (colour && variants[colour]) || Object.values(variants)[0];
      if (variantIdInput && variantId) variantIdInput.value = variantId;

      if (lowStock) {
        let lowMap = {};
        try { lowMap = JSON.parse(lowStock.dataset.lowStockMap || '{}'); } catch (_) {}
        lowStock.hidden = !lowMap[String(variantIdInput ? variantIdInput.value : variantId)];
      }

      if (priceCurrent) priceCurrent.textContent = selected.dataset.price || '';
      if (priceCompare) {
        priceCompare.textContent = selected.dataset.compare || '';
        priceCompare.hidden = !selected.dataset.compare;
      }
      if (priceSave) {
        priceSave.textContent = selected.dataset.save || '';
        priceSave.hidden = !selected.dataset.save;
      }
    }

    // ?bundle=1 / ?bundle=2 (the homepage "Buy two" section links here
    // with its chosen option) pre-selects that card.
    const requestedBundle = new URLSearchParams(window.location.search).get('bundle');
    const requestedCard = requestedBundle
      ? document.querySelector(`[data-bundle-card][data-bundle-key="${CSS.escape(requestedBundle)}"]`)
      : null;
    if (requestedCard) {
      bundleCards.forEach(c => c.removeAttribute('data-selected'));
      requestedCard.setAttribute('data-selected', '');
      syncBundleCTA();
    }

    bundleCards.forEach(card => {
      card.addEventListener('click', () => {
        bundleCards.forEach(c => c.removeAttribute('data-selected'));
        card.setAttribute('data-selected', '');
        syncBundleCTA();
      });
      card.addEventListener('keydown', e => {
        if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); card.click(); }
      });
    });

    // Sticky cart bar — appears when main CTA button is out of view
    const stickyBar = document.getElementById('sticky-cart-bar');
    const ctaBtn    = document.getElementById('product-cta');

    if (stickyBar && ctaBtn) {
      const obs = new IntersectionObserver(
        ([entry]) => stickyBar.classList.toggle('visible', !entry.isIntersecting),
        { threshold: 0, rootMargin: '-64px 0px 0px 0px' }
      );
      obs.observe(ctaBtn);
    }

    // Main form + sticky CTA — AJAX add-to-cart → open drawer
    async function addToCartAjax() {
      const form = document.getElementById('product-form');
      if (!form) return;
      const btn = productCta || stickyCta;
      const origText = btn ? btn.textContent : '';
      if (btn) { btn.disabled = true; btn.textContent = btn.dataset.addingLabel || 'Adding…'; }
      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          body: new FormData(form),
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });
        if (res.ok) {
          document.dispatchEvent(new CustomEvent('block:cart:open'));
        }
      } catch (_) {}
      if (btn) { btn.disabled = false; btn.textContent = origText; }
    }

    const productForm = document.getElementById('product-form');
    if (productForm) {
      productForm.addEventListener('submit', e => {
        e.preventDefault();
        addToCartAjax();
      });
    }

    if (stickyCta) {
      stickyCta.addEventListener('click', addToCartAjax);
    }

    // Collapsible product description (enterphase.app-style). A single
    // toggle, not an accordion group - reuses .block-faq-body's own
    // open/close CSS (§12) but its own data-description-* hooks, kept
    // deliberately separate from setupFAQ()'s data-faq-* ones so opening
    // this doesn't also collapse whatever FAQ answer is open elsewhere
    // on the same page (setupFAQ() closes every [data-faq-item] globally).
    const descTrigger = document.querySelector('[data-description-trigger]');
    const descBody    = document.querySelector('[data-description-body]');
    if (descTrigger && descBody) {
      descTrigger.addEventListener('click', () => {
        const isOpen = descBody.classList.contains('open');
        descBody.classList.toggle('open', !isOpen);
        descBody.setAttribute('aria-hidden', String(isOpen));
        descTrigger.setAttribute('aria-expanded', String(!isOpen));
        const icon = descTrigger.querySelector('svg');
        if (icon) icon.style.transform = isOpen ? 'none' : 'rotate(180deg)';
      });
    }
  }

  // ── 10. Cart Drawer ────────────────────────────────────────
  function setupCartDrawer() {
    const overlay  = document.getElementById('block-cart-overlay');
    const drawer   = document.getElementById('block-cart-drawer');
    const closeBtn = document.getElementById('block-cart-drawer-close');
    const cartBtn  = document.getElementById('block-nav-cart-btn');

    if (!drawer) return;

    const stringsEl = document.getElementById('block-cart-strings');
    const strings    = stringsEl ? JSON.parse(stringsEl.textContent) : {};

    // ── Tabs ──────────────────────────────────────────────────
    const tabs = drawer.querySelectorAll('.block-cart-drawer__tab');
    tabs.forEach(tab => {
      tab.addEventListener('click', () => {
        tabs.forEach(t => {
          t.classList.remove('is-active');
          t.setAttribute('aria-selected', 'false');
        });
        tab.classList.add('is-active');
        tab.setAttribute('aria-selected', 'true');

        drawer.querySelectorAll('.block-cart-drawer__panel').forEach(p => {
          p.style.display = 'none';
          p.setAttribute('aria-hidden', 'true');
        });
        const panel = document.getElementById(`block-cart-tab-${tab.dataset.tab}`);
        if (panel) {
          panel.style.display = '';
          panel.removeAttribute('aria-hidden');
        }

        if (tab.dataset.tab === 'recently-viewed') renderRecentlyViewed();
      });
    });

    // ── Open / close ──────────────────────────────────────────
    function openDrawer() {
      fetchAndRender();
      drawer.classList.add('open');
      drawer.setAttribute('aria-hidden', 'false');
      if (overlay) overlay.classList.add('open');
      document.body.style.overflow = 'hidden';
      setTimeout(() => closeBtn && closeBtn.focus(), 40);
    }

    function closeDrawer() {
      drawer.classList.remove('open');
      drawer.setAttribute('aria-hidden', 'true');
      if (overlay) overlay.classList.remove('open');
      document.body.style.overflow = '';
    }

    if (cartBtn)  cartBtn.addEventListener('click', openDrawer);
    if (closeBtn) closeBtn.addEventListener('click', closeDrawer);
    if (overlay)  overlay.addEventListener('click', closeDrawer);
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && drawer.classList.contains('open')) closeDrawer();
    });
    document.addEventListener('block:cart:open', openDrawer);

    // ── Helpers ───────────────────────────────────────────────
    function money(cents, currency) {
      return new Intl.NumberFormat(strings.locale, { style: 'currency', currency: currency }).format(cents / 100);
    }

    function syncCount(count) {
      const badge      = document.getElementById('block-nav-cart-count');
      const tabCount   = document.getElementById('block-cart-drawer-count');
      if (badge) {
        badge.textContent   = count;
        badge.style.display = count > 0 ? 'flex' : 'none';
      }
      if (tabCount) tabCount.textContent = count > 0 ? count : '';
    }

    // ── Cart fetch & render ───────────────────────────────────
    async function fetchAndRender() {
      const itemsEl  = document.getElementById('block-cart-drawer-items');
      const extrasEl = document.getElementById('block-cart-drawer-extras');
      const footerEl = document.getElementById('block-cart-drawer-footer');
      if (!itemsEl) return;

      itemsEl.innerHTML = `<div style="padding:32px 24px;font-size:13px;color:rgba(255,255,255,0.25);">${strings.loading}</div>`;
      if (extrasEl) extrasEl.innerHTML = '';
      if (footerEl) footerEl.innerHTML = '';

      const res  = await fetch('/cart.js');
      const cart = await res.json();

      syncCount(cart.item_count);
      renderItems(cart, itemsEl);
      if (cart.item_count > 0) {
        renderExtras(cart, extrasEl);
        renderFooter(cart, footerEl);
      }
    }

    function renderItems(cart, el) {
      if (!el) return;
      if (cart.item_count === 0) {
        el.innerHTML = `
          <div class="block-cart-drawer__empty">
            <p>${strings.emptyText}</p>
            <a href="/collections/all" class="block-btn-primary">${strings.emptyCta}</a>
          </div>`;
        return;
      }

      el.innerHTML = cart.items.map(item => `
        <div class="block-cart-drawer__item">
          <div class="block-cart-drawer__item-img">
            ${item.image
              ? `<img src="${item.image.replace('_original', '_200x200')}" alt="${item.product_title}" width="72" height="72" loading="lazy">`
              : ''}
          </div>
          <div class="block-cart-drawer__item-body">
            <div class="block-cart-drawer__item-top">
              <span class="block-cart-drawer__item-name">${item.product_title}</span>
              <span class="block-cart-drawer__item-price">${money(item.final_line_price, cart.currency)}</span>
            </div>
            ${item.variant_title && item.variant_title !== 'Default Title'
              ? `<span class="block-cart-drawer__item-variant">${item.variant_title}</span>`
              : ''}
            <div class="block-cart-drawer__item-footer">
              <div class="block-cart-qty">
                <button class="block-cart-qty__btn" data-key="${item.key}" data-qty="${item.quantity - 1}" aria-label="${strings.qtyDecreaseAria}">−</button>
                <span class="block-cart-qty__num">${item.quantity}</span>
                <button class="block-cart-qty__btn" data-key="${item.key}" data-qty="${item.quantity + 1}" aria-label="${strings.qtyIncreaseAria}">+</button>
              </div>
              <button class="block-cart-drawer__remove" data-key="${item.key}">${strings.removeLabel}</button>
            </div>
          </div>
        </div>`).join('');

      el.querySelectorAll('.block-cart-qty__btn').forEach(btn => {
        btn.addEventListener('click', () => changeItem(btn.dataset.key, parseInt(btn.dataset.qty, 10)));
      });
      el.querySelectorAll('.block-cart-drawer__remove').forEach(btn => {
        btn.addEventListener('click', () => changeItem(btn.dataset.key, 0));
      });
    }

    function renderExtras(cart, el) {
      if (!el) return;
      el.innerHTML = `
        <div class="block-cart-extras">
          <div class="block-cart-extras__row">
            <button class="block-cart-extras__toggle" type="button">
              <span class="block-cart-extras__icon">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 12 20 22 4 22 4 12"/><rect x="2" y="7" width="20" height="5"/><path d="M12 22V7"/><path d="M12 7H7.5a2.5 2.5 0 010-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 000-5C13 2 12 7 12 7z"/></svg>
              </span>
              ${strings.discountToggleLabel}
              <svg class="block-cart-extras__chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M6 9l6 6 6-6"/></svg>
            </button>
            <div class="block-cart-extras__body" hidden>
              <div class="block-cart-discount-row">
                <input type="text" id="block-cart-discount-input" class="block-cart-input" placeholder="${strings.discountPlaceholder}">
                <button id="block-cart-discount-btn" class="block-cart-input-btn" type="button">${strings.discountApplyLabel}</button>
              </div>
            </div>
          </div>
          <div class="block-cart-extras__row">
            <button class="block-cart-extras__toggle" type="button">
              <span class="block-cart-extras__icon">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
              </span>
              ${strings.noteToggleLabel}
              <svg class="block-cart-extras__chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M6 9l6 6 6-6"/></svg>
            </button>
            <div class="block-cart-extras__body" hidden>
              <textarea id="block-cart-note-input" class="block-cart-input" rows="3" placeholder="${strings.notePlaceholder}">${cart.note || ''}</textarea>
            </div>
          </div>
        </div>`;

      el.querySelectorAll('.block-cart-extras__toggle').forEach(btn => {
        btn.addEventListener('click', () => {
          const body    = btn.nextElementSibling;
          const isOpen  = !body.hidden;
          body.hidden   = isOpen;
          const chevron = btn.querySelector('.block-cart-extras__chevron');
          if (chevron) chevron.style.transform = isOpen ? '' : 'rotate(180deg)';
        });
      });

      const discountBtn = el.querySelector('#block-cart-discount-btn');
      if (discountBtn) {
        discountBtn.addEventListener('click', () => {
          const code = el.querySelector('#block-cart-discount-input').value.trim();
          if (code) window.location.href = `/checkout?discount=${encodeURIComponent(code)}`;
        });
      }

      const noteInput = el.querySelector('#block-cart-note-input');
      if (noteInput) {
        let noteTimer;
        noteInput.addEventListener('input', () => {
          clearTimeout(noteTimer);
          noteTimer = setTimeout(() => {
            fetch('/cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ note: noteInput.value }),
            });
          }, 800);
        });
      }
    }

    function renderFooter(cart, el) {
      if (!el) return;
      el.innerHTML = `
        <div class="block-cart-footer__subtotal">
          <span>${strings.subtotalLabel}</span>
          <span class="block-cart-footer__price">${money(cart.total_price, cart.currency)}</span>
        </div>
        <p class="block-cart-footer__meta">${strings.taxNoteText}</p>
        <a href="/checkout" class="block-btn-primary block-cart-footer__checkout">${strings.checkoutLabel}</a>
        <div class="block-cart-footer__view-cart"><a href="/cart">${strings.viewCartLabel}</a></div>`;
    }

    async function changeItem(key, quantity) {
      const itemsEl  = document.getElementById('block-cart-drawer-items');
      const extrasEl = document.getElementById('block-cart-drawer-extras');
      const footerEl = document.getElementById('block-cart-drawer-footer');
      if (itemsEl) itemsEl.style.opacity = '0.4';

      const res  = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity }),
      });
      const cart = await res.json();

      if (itemsEl) itemsEl.style.opacity = '1';
      syncCount(cart.item_count);
      renderItems(cart, itemsEl);
      if (cart.item_count > 0) {
        renderExtras(cart, extrasEl);
        renderFooter(cart, footerEl);
      } else {
        if (extrasEl) extrasEl.innerHTML = '';
        if (footerEl) footerEl.innerHTML = '';
      }
    }

    // ── Recently viewed ───────────────────────────────────────
    const productData = window.__blockProduct;
    if (productData) trackRecentlyViewed(productData);

    function trackRecentlyViewed(product) {
      try {
        const stored   = JSON.parse(localStorage.getItem('block_rv') || '[]');
        const filtered = stored.filter(p => p.handle !== product.handle);
        localStorage.setItem('block_rv', JSON.stringify([product, ...filtered].slice(0, 8)));
      } catch (_) {}
    }

    function renderRecentlyViewed() {
      const el = document.getElementById('block-cart-recently-viewed');
      if (!el) return;
      let items = [];
      try { items = JSON.parse(localStorage.getItem('block_rv') || '[]'); } catch (_) {}

      if (items.length === 0) {
        el.innerHTML = `<div class="block-cart-drawer__empty"><p>${strings.recentlyViewedEmptyText}</p></div>`;
        return;
      }

      el.innerHTML = items.map(p => `
        <a href="${p.url}" class="block-cart-rv-item">
          <div class="block-cart-drawer__item-img">
            ${p.image ? `<img src="${p.image}" alt="${p.title}" width="72" height="72" loading="lazy">` : ''}
          </div>
          <div class="block-cart-rv-item__info">
            <span class="block-cart-rv-item__name">${p.title}</span>
            <span class="block-cart-rv-item__price">${p.price}</span>
          </div>
        </a>`).join('');
    }
  }

  // ── Real Stories video slider ─────────────────────────────
  // Poster-with-play-button sits on top of a <video> that's already in the
  // DOM (see block-real-stories.liquid) — clicking just fades the poster
  // out and calls .play(), no fetch/injection needed. Starting one card
  // pauses whichever other card was mid-playback, and the prev/next
  // buttons scroll the native scroll-snap track by one card width.
  function setupStoryCards() {
    const track = document.querySelector('[data-stories-track]');
    if (!track) return;

    track.querySelectorAll('[data-story-card]').forEach(card => {
      const playBtn = card.querySelector('[data-story-play]');
      const video   = card.querySelector('[data-story-video]');
      if (!playBtn || !video) return;

      playBtn.addEventListener('click', () => {
        track.querySelectorAll('[data-story-card].is-playing').forEach(other => {
          if (other === card) return;
          other.classList.remove('is-playing');
          const otherVideo = other.querySelector('[data-story-video]');
          if (otherVideo) otherVideo.pause();
        });
        card.classList.add('is-playing');
        video.play();
      });

      video.addEventListener('ended', () => {
        card.classList.remove('is-playing');
        video.currentTime = 0;
      });
    });

    function scrollByCard(dir) {
      const card = track.querySelector('[data-story-card]');
      if (!card) return;
      const gap = parseFloat(getComputedStyle(track).gap) || 0;
      track.scrollBy({ left: (card.getBoundingClientRect().width + gap) * dir, behavior: 'smooth' });
    }
    const prevBtn = document.querySelector('[data-stories-prev]');
    const nextBtn = document.querySelector('[data-stories-next]');
    if (prevBtn) prevBtn.addEventListener('click', () => scrollByCard(-1));
    if (nextBtn) nextBtn.addEventListener('click', () => scrollByCard(1));
  }

  // ── Outcomes scroll-pin ────────────────────────────────────
  // block-outcomes-scroll.liquid gives the track extra height (100vh +
  // 60vh per item) purely as scroll runway; the pin itself is plain CSS
  // position:sticky (see block-design.css §26). This just maps how far
  // the track has scrolled past into an active item index, on desktop
  // only — mobile drops the whole track height/pin (see the same CSS
  // file's mobile override), so there's nothing meaningful to compute
  // there and the scroll listener would just be dead weight.
  function setupOutcomesScroll() {
    const track = document.querySelector('[data-outcomes-scroll]');
    if (!track) return;

    const triggers = track.querySelectorAll('[data-outcome-trigger]');
    const images   = track.querySelectorAll('[data-outcome-image]');
    const count    = triggers.length;
    if (!count) return;

    const mq = window.matchMedia('(min-width: 769px)');

    function setActive(index) {
      triggers.forEach((el, i) => el.classList.toggle('is-active', i === index));
      images.forEach((el, i) => el.classList.toggle('is-active', i === index));
    }

    function update() {
      if (!mq.matches) return;
      const rect = track.getBoundingClientRect();
      const scrollable = rect.height - window.innerHeight;
      if (scrollable <= 0) { setActive(0); return; }
      const progress = Math.min(1, Math.max(0, -rect.top / scrollable));
      let index = Math.floor(progress * count);
      if (index >= count) index = count - 1;
      setActive(index);
    }

    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();
  }

  // ── block-what-changes.liquid scroll-linked media pin ─────
  // Pins a small photo (position:fixed, not sticky - same ancestor-
  // overflow:hidden reason as setupJourneyScroll below) at a position
  // that's genuinely CONSTANT on screen while scrolling - viewport-
  // center vertically, the real 1440px content edge horizontally - set
  // once on init/resize (positionPin) and never touched by the scroll
  // handler. Matches cv.rocks' own reference behaviour this was modeled
  // on: the photo box itself never moves; only its CONTENT changes
  // (which image is active) depending on which row is currently
  // centered. An earlier version instead moved the box to chase the
  // active row every scroll tick, eased via a CSS transition - that
  // read as a laggy "magnetic" pull toward each row instead of a truly
  // fixed element, so both the per-tick repositioning and the
  // transition were removed.
  //
  // The pin is visually much bigger than a single row's text though
  // (clamp 200-300px wide, 3:4 aspect ratio), so it typically overlaps
  // 2-3 adjacent rows' title/description text at once - .is-media-active
  // (which triggers the mix-blend-mode:difference knockout in CSS) is
  // applied independently to every title AND every description the
  // pin's own (now-static) rect actually overlaps, not just whichever
  // row is nearest center, so the photo reads as genuinely behind
  // whatever real text it's visually behind at any given moment.
  function setupOutcomesMedia() {
    const list   = document.querySelector('[data-outcomes-media-list]');
    const pin    = document.querySelector('[data-outcomes-media-pin]');
    const anchor = document.querySelector('[data-outcomes-media-anchor]');
    if (!list || !pin || !anchor) return;

    const rows   = list.querySelectorAll('[data-outcomes-media-row]');
    const images = pin.querySelectorAll('[data-outcomes-media-image]');
    const count  = rows.length;
    if (!count) return;

    const mq = window.matchMedia('(max-width: 768px)');

    function setActiveImage(index) {
      images.forEach(el => el.classList.toggle('is-active', el.dataset.index === String(index)));
    }

    function clearAllTitles() {
      rows.forEach(row => {
        const title = row.querySelector('[data-outcomes-media-title]');
        if (title) title.classList.remove('is-media-active');
        const desc = row.querySelector('[data-outcomes-media-desc]');
        if (desc) desc.classList.remove('is-media-active');
      });
    }

    function rectsOverlap(a, b) {
      return a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;
    }

    // Fixed on-screen position, recomputed only when the viewport
    // itself changes - never on scroll.
    function positionPin() {
      if (mq.matches) return;
      const anchorRect = anchor.getBoundingClientRect();
      const halfWidth = pin.offsetWidth / 2;
      pin.style.left = (anchorRect.right - halfWidth) + 'px';
      pin.style.top = (window.innerHeight / 2) + 'px';
    }

    function update() {
      if (mq.matches) {
        pin.classList.remove('is-visible');
        clearAllTitles();
        return;
      }

      const listRect = list.getBoundingClientRect();
      const viewportCenter = window.innerHeight / 2;

      if (listRect.top > viewportCenter || listRect.bottom < viewportCenter) {
        pin.classList.remove('is-visible');
        clearAllTitles();
        return;
      }
      pin.classList.add('is-visible');

      let activeIndex = 0;
      let closestDistance = Infinity;
      rows.forEach((row, i) => {
        const title = row.querySelector('[data-outcomes-media-title]');
        if (!title) return;
        const rect = title.getBoundingClientRect();
        const distance = Math.abs((rect.top + rect.height / 2) - viewportCenter);
        if (distance < closestDistance) {
          closestDistance = distance;
          activeIndex = i;
        }
      });

      setActiveImage(activeIndex);

      // Pin's own position is static now (see positionPin) - safe to
      // read its real rect directly, no risk of catching a mid-
      // transition/stale value.
      const pinRect = pin.getBoundingClientRect();
      rows.forEach(row => {
        const title = row.querySelector('[data-outcomes-media-title]');
        if (title) title.classList.toggle('is-media-active', rectsOverlap(title.getBoundingClientRect(), pinRect));
        const desc = row.querySelector('[data-outcomes-media-desc]');
        if (desc) desc.classList.toggle('is-media-active', rectsOverlap(desc.getBoundingClientRect(), pinRect));
      });
    }

    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', () => { positionPin(); update(); });
    positionPin();
    update();
  }

  // ── block-what-changes-scroll.liquid scroll pin ───────────
  // Deliberately NOT position:sticky - see the comment at the top of
  // block-what-changes-scroll.liquid for why (sticky needs every
  // ancestor up to the real page scroll container to have overflow:
  // visible, but the canvas system's .shopify-section wrapper always
  // has overflow:hidden for its rounded corners; overriding that broke
  // the flex `gap` for every section after it, on the earlier
  // block-outcomes-scroll.liquid attempt). position:fixed isn't
  // restricted by ancestor overflow the same way, so this pins
  // .block-journey__pin (the WHOLE card - text and media together) by
  // hand instead: measures [data-journey-track]'s position on scroll
  // and toggles the pin between position:static (before), fixed (while
  // scrolling through the track), and absolute/bottom:0 (after) - a
  // classic three-state scroll-jack. .block-journey__track is a
  // deliberately-tall, otherwise-empty spacer that exists purely to
  // give this something to scroll through; the pin's own height is
  // never forced, it just sizes to its natural content in all 3 states.
  function setupJourneyScroll() {
    const track = document.querySelector('[data-journey-track]');
    const pin   = document.querySelector('[data-journey-pin]');
    if (!track || !pin) return;

    const items  = pin.querySelectorAll('[data-journey-item]');
    const images = pin.querySelectorAll('[data-journey-image]');
    const pills  = pin.querySelectorAll('[data-journey-pill]');
    const count  = items.length;
    if (!count) return;

    // px of scroll distance per item, NOT vh - vh is relative to the
    // viewport's own height, so the same vh number is a lot more actual
    // pixels on a large/tall monitor than on a laptop, making the track
    // (and the whole section) grow disproportionately taller there and
    // leaving a big empty gap once the card finished cycling through
    // its items. A flat px budget scales with content, not viewport.
    const PER_ITEM_PX = 200;

    const mq = window.matchMedia('(max-width: 768px)');
    // Same clearance formula already used elsewhere for "how far below
    // the floating nav should something pinned start" (e.g. the earlier
    // block-outcomes-scroll.liquid's own --canvas-gap + --nav-height +
    // 24px pin offset) - read from the actual CSS tokens rather than
    // hardcoded, so it stays right if those ever change. A flat 24px
    // (this section's original value) put the pinned card's top edge
    // inside the nav's own footprint (nav sits at 12px, is 64px tall -
    // 24px alone doesn't clear that).
    const rootStyle = getComputedStyle(document.documentElement);
    const canvasGap = parseFloat(rootStyle.getPropertyValue('--canvas-gap')) || 12;
    const navHeight = parseFloat(rootStyle.getPropertyValue('--nav-height')) || 64;
    const PIN_OFFSET = canvasGap + navHeight + 24;

    function setActive(index) {
      items.forEach((el, i) => el.classList.toggle('is-active', i === index));
      images.forEach((el, i) => el.classList.toggle('is-active', i === index));
      // Matched by data-index, not NodeList position - unlike items/images
      // (always one per row), a pill only renders when that row has a
      // description, so its NodeList can be sparser than the row count.
      pills.forEach(el => el.classList.toggle('is-active', el.dataset.index === String(index)));
    }

    function reset() {
      pin.style.position = '';
      pin.style.top = '';
      pin.style.bottom = '';
      pin.style.left = '';
      pin.style.width = '';
    }

    // Sets the track's height in real px, once (not on every scroll
    // tick - changing an element's height while the user is mid-scroll
    // through it would shift the page under them, which is exactly the
    // kind of jank scroll-jacking sections need to avoid). Re-run only
    // on resize, alongside update().
    function sizeTrack() {
      if (mq.matches) { track.style.height = ''; return; }
      track.style.height = (pin.offsetHeight + PER_ITEM_PX * count) + 'px';
    }

    function update() {
      if (mq.matches) { reset(); setActive(0); return; }

      const trackRect = track.getBoundingClientRect();
      const pinHeight = pin.offsetHeight; // natural content height, stable across all 3 position modes since none of them force a height
      const scrollable = trackRect.height - pinHeight;

      if (scrollable <= 0 || trackRect.top > PIN_OFFSET) {
        // Track hasn't reached the pin line yet - natural position.
        reset();
        setActive(0);
        return;
      }

      if (trackRect.bottom - pinHeight < PIN_OFFSET) {
        // Track has fully scrolled past - release, parked at the
        // bottom of the track (which has position:relative) so it ends
        // flush with the track's own end instead of snapping away.
        pin.style.position = 'absolute';
        pin.style.top = '';
        pin.style.bottom = '0';
        pin.style.left = '0';
        pin.style.width = '100%';
        setActive(count - 1);
        return;
      }

      // Mid-track - pin at a fixed viewport position, width matched to
      // the track's own (so it lines up with where it'd normally sit).
      pin.style.position = 'fixed';
      pin.style.top = PIN_OFFSET + 'px';
      pin.style.bottom = '';
      pin.style.left = trackRect.left + 'px';
      pin.style.width = trackRect.width + 'px';

      const progress = Math.min(1, Math.max(0, (PIN_OFFSET - trackRect.top) / scrollable));
      let index = Math.floor(progress * count);
      if (index >= count) index = count - 1;
      setActive(index);
    }

    // Click-to-jump: scrolls the page so a given item lands mid-track at
    // exactly the progress that makes update() pick it as active - not
    // scrollIntoView() (doesn't account for the fixed nav/PIN_OFFSET
    // clearance and can't target a scroll-progress fraction inside a
    // track), same getBoundingClientRect()+scrollY+scrollTo() pattern
    // used elsewhere in this codebase for offset-aware scrolling.
    function scrollToItem(index) {
      if (mq.matches) return; // mobile: no track/pin, items are already all expanded

      const trackRect = track.getBoundingClientRect();
      const scrollable = trackRect.height - pin.offsetHeight;
      if (scrollable <= 0) return;

      // Middle of this item's own slice of the progress range (0..1),
      // so the jump lands solidly inside its window rather than right
      // on a boundary edge with the next item.
      const progress = (index + 0.5) / count;
      const trackAbsoluteTop = trackRect.top + window.scrollY;
      const targetScrollY = trackAbsoluteTop - PIN_OFFSET + progress * scrollable;

      setActive(index); // instant feedback, ahead of the smooth-scroll animation
      window.scrollTo({ top: targetScrollY, behavior: 'smooth' });
    }

    items.forEach((el, i) => {
      el.addEventListener('click', () => scrollToItem(i));
      el.addEventListener('keydown', event => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          scrollToItem(i);
        }
      });
    });

    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', () => { sizeTrack(); update(); });
    sizeTrack();
    update();
  }

  // ── block-comparison.liquid: sideways-scroll affordance (phones) ──
  // Toggles .has-more-right on the card (drives the right-edge fade in
  // the section's CSS) and plays a one-time nudge - the table scrolls a
  // few dozen px right and back - the first time it is mostly in view.
  // Skipped when the user already scrolled/touched it or prefers
  // reduced motion. No-op on >=900px where the table doesn't scroll
  // sideways (the pin mechanic drives it vertically there).
  function setupComparisonSwipeHint() {
    const scroll = document.querySelector('[data-comparison-scroll]');
    const card = scroll && scroll.closest('.block-comparison-card');
    if (!scroll || !card) return;

    const updateFade = () => {
      const more = scroll.scrollWidth - scroll.clientWidth > 4 &&
                   scroll.scrollLeft + scroll.clientWidth < scroll.scrollWidth - 4;
      card.classList.toggle('has-more-right', more);
    };
    scroll.addEventListener('scroll', updateFade, { passive: true });
    window.addEventListener('resize', updateFade);
    updateFade();

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let played = false, interrupted = false;
    const stop = () => { interrupted = true; };
    ['touchstart', 'wheel', 'mousedown'].forEach(ev => scroll.addEventListener(ev, stop, { passive: true }));

    function nudge() {
      if (played || interrupted || reduce) return;
      if (scroll.scrollWidth - scroll.clientWidth < 40 || scroll.scrollLeft > 0) return;
      played = true;
      const dist = 56, dur = 1300, t0 = performance.now();
      const ease = t => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);
      (function step(now) {
        if (interrupted) return;
        const t = Math.min(1, (now - t0) / dur);
        // 0 -> dist -> 0 (triangle wave, eased)
        scroll.scrollLeft = dist * ease(t < 0.5 ? t * 2 : (1 - t) * 2);
        if (t < 1) requestAnimationFrame(step); else scroll.scrollLeft = 0;
      })(t0);
    }

    const check = () => {
      const r = card.getBoundingClientRect();
      const vh = window.innerHeight;
      const visible = Math.min(r.bottom, vh) - Math.max(r.top, 0);
      if (visible > Math.min(r.height, vh) * 0.6) { nudge(); if (played) cleanup(); }
    };
    const cleanup = () => window.removeEventListener('scroll', check);
    window.addEventListener('scroll', check, { passive: true });
    check();
  }

  // ── block-comparison.liquid scroll-pin ──────────────────────
  // Same pin/track mechanic as setupJourneyScroll above (position:
  // static/fixed/absolute toggled on .block-comparison-pin, sized via
  // an invisible .block-comparison-track spacer, never CSS position:
  // sticky), but this section doesn't cycle through discrete items -
  // it holds the page's own scroll still and uses that scroll delta to
  // drive .block-comparison-scroll's scrollTop directly instead, so
  // the table's rows advance in lockstep with the page scroll instead
  // of the user's cursor-over-the-table wheel input scrolling that div
  // natively (which is what caused the springy "elastic"/rubber-band
  // feel - see the overflow-y:hidden comment on .block-comparison-
  // scroll in block-comparison.liquid's own <style> block).
  function setupComparisonScroll() {
    const track   = document.querySelector('[data-comparison-track]');
    const pin     = document.querySelector('[data-comparison-pin]');
    const scroll  = document.querySelector('[data-comparison-scroll]');
    const tbody   = document.querySelector('[data-comparison-tbody]');
    if (!track || !pin || !scroll || !tbody) return;

    const mq = window.matchMedia('(max-width: 899px)');

    // Section's own top gap (12px) - the card is meant to use the full
    // viewport height, sitting behind the floating nav while pinned
    // just like every other (non-pinned) section already does while
    // scrolling past it. An earlier version reserved extra nav-height
    // clearance here to dodge the nav's own backdrop-filter:blur(12px),
    // but the actual header-readability problem that motivated that was
    // the thead/tbody stacking-order + transparent-border bugs (see
    // block-comparison.liquid's own <style> block), not the nav overlap
    // itself, so the full-height card is safe to restore.
    const rootStyle = getComputedStyle(document.documentElement);
    const canvasGap = parseFloat(rootStyle.getPropertyValue('--canvas-gap')) || 12;
    const PIN_OFFSET = canvasGap;

    function reset() {
      pin.style.position = '';
      pin.style.top = '';
      pin.style.bottom = '';
      pin.style.left = '';
      pin.style.width = '';
    }

    function setTbodyOffset(px) {
      tbody.style.transform = px > 0 ? `translateY(-${px}px)` : '';
    }

    // Pin's own height is the SMALLER of the viewport's available space
    // and the card's actual natural content height - on a tall enough
    // monitor where the heading+table already fit well under
    // 100vh-24px, the pin should just hug that natural height instead
    // of stretching to fill the extra room (which would otherwise show
    // up as dead white space below the last row, inside the card).
    // Measured by briefly clearing the height (letting the box size to
    // its own content - .block-comparison-wrap's height:100% and every
    // flex:1 below it all fall back to natural sizing once their
    // ancestor's own height isn't a definite value) and reading the
    // result back before settling on the smaller of the two - this is a
    // synchronous read-after-write, so nothing actually paints in
    // between and there's no visible flash.
    function applyPinHeight() {
      if (mq.matches) { pin.style.height = ''; return; }
      pin.style.height = 'auto';
      const natural = pin.scrollHeight;
      const available = window.innerHeight - canvasGap * 2;
      pin.style.height = Math.min(natural, available) + 'px';
    }

    // How far the row group needs to travel to bring the last row into
    // view - measured off the WHOLE table (thead + tbody), since that's
    // what .block-comparison-scroll's overflow:hidden is clipping. The
    // header row itself never moves (see the CSS comment on .block-
    // comparison-scroll), only the row group is offset upward by this
    // amount at full progress.
    function maxOffset() {
      return Math.max(0, scroll.scrollHeight - scroll.clientHeight);
    }

    // Track's height = the pin's own fixed height + however many extra
    // px the table overflows it by - that overflow amount becomes the
    // extra scroll distance the page has to travel through while
    // "held" on this section, one page-scroll px per one row-group
    // translate px. Set once (not per scroll tick, matching
    // setupJourneyScroll's own sizeTrack), re-run only on resize.
    function sizeTrack() {
      if (mq.matches) { track.style.height = ''; return; }
      applyPinHeight();
      track.style.height = (pin.offsetHeight + maxOffset()) + 'px';
    }

    function update() {
      if (mq.matches) { reset(); setTbodyOffset(0); return; }

      const trackRect = track.getBoundingClientRect();
      const pinHeight = pin.offsetHeight; // stable across all 3 position modes - height is an explicit CSS value, not derived from content
      const scrollable = trackRect.height - pinHeight;

      if (scrollable <= 0 || trackRect.top > PIN_OFFSET) {
        // Track hasn't reached the pin line yet - natural position, table at its first row.
        reset();
        setTbodyOffset(0);
        return;
      }

      if (trackRect.bottom - pinHeight < PIN_OFFSET) {
        // Track has fully scrolled past - release, parked at the
        // bottom of the track (position:relative) so it ends flush
        // with the track's own end, table left showing its last row.
        pin.style.position = 'absolute';
        pin.style.top = '';
        pin.style.bottom = '0';
        pin.style.left = '0';
        pin.style.width = '100%';
        setTbodyOffset(maxOffset());
        return;
      }

      // Mid-track - pin held at a fixed viewport position, width
      // matched to the track's own so it lines up with where it'd
      // normally sit; the page's scroll progress through the track
      // drives how far the row group is translated.
      pin.style.position = 'fixed';
      pin.style.top = PIN_OFFSET + 'px';
      pin.style.bottom = '';
      pin.style.left = trackRect.left + 'px';
      pin.style.width = trackRect.width + 'px';

      const progress = Math.min(1, Math.max(0, (PIN_OFFSET - trackRect.top) / scrollable));
      setTbodyOffset(progress * maxOffset());
    }

    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', () => { sizeTrack(); update(); });
    sizeTrack();
    update();
  }

  // ── block-comparison.liquid info tooltips ───────────────────
  // "Physical barrier" and "Essentials stay reachable" row labels each
  // carry an [data-comp-tooltip-trigger] button. A single shared
  // popover (position:fixed, positioned here off the trigger's own
  // getBoundingClientRect()) is reused for both rather than one
  // popover per trigger - simpler state (only one can ever be open)
  // and avoids needing N separately-positioned elements. position:
  // fixed is required, not just convenient: this table sits inside
  // .block-comparison-scroll/.block-comparison-card/.block-comparison-
  // pin-inner, all overflow:hidden (that's what clips the scroll-pin's
  // row content, see block-comparison.liquid's own <style> block) - an
  // absolutely-positioned popover anchored to the trigger would get
  // silently clipped by those the moment it tried to extend past a
  // cell's own bounds. Same escape-hatch already used for the pin
  // itself while actively pinned.
  function setupComparisonTooltips() {
    const triggers = document.querySelectorAll('[data-comp-tooltip-trigger]');
    if (!triggers.length) return;

    const popover = document.createElement('div');
    popover.className = 'comp-tooltip-popover';
    popover.setAttribute('role', 'tooltip');
    document.body.appendChild(popover);

    let active = null;

    function position(trigger) {
      const r = trigger.getBoundingClientRect();
      const pw = popover.offsetWidth;
      let left = r.left + r.width / 2 - pw / 2;
      left = Math.max(8, Math.min(left, window.innerWidth - pw - 8));
      popover.style.left = left + 'px';
      popover.style.top = (r.bottom + 8) + 'px';
    }

    function show(trigger) {
      active = trigger;
      popover.textContent = trigger.dataset.tooltip;
      popover.classList.add('is-visible');
      position(trigger); // measure AFTER the text is set, so offsetWidth reflects this trigger's own content, not the previous one's
    }

    function hide() {
      active = null;
      popover.classList.remove('is-visible');
    }

    triggers.forEach(trigger => {
      trigger.addEventListener('mouseenter', () => show(trigger));
      trigger.addEventListener('mouseleave', hide);
      trigger.addEventListener('focus', () => show(trigger));
      trigger.addEventListener('blur', hide);
      // Tap-to-toggle for touch devices, where hover/focus don't
      // reliably map to a single clear "open" gesture the way mouse
      // hover or keyboard focus do.
      trigger.addEventListener('click', e => {
        e.preventDefault();
        if (active === trigger) { hide(); } else { show(trigger); }
      });
    });

    window.addEventListener('scroll', () => { if (active) position(active); }, { passive: true });
    window.addEventListener('resize', () => { if (active) position(active); });
    document.addEventListener('click', e => {
      if (active && !e.target.closest('[data-comp-tooltip-trigger]')) hide();
    });
  }

  // ── Drag-to-scroll ──────────────────────────────────────────
  // Generic click-and-drag scrolling for any [data-drag-scroll] element
  // (product-hero's gallery thumbnails + video-review track, currently) -
  // touch/trackpad already scroll a plain overflow-x:auto natively, this
  // is purely for mouse users, who otherwise have no way to drag a
  // horizontal scroller. A .is-dragging class lets each element's own
  // CSS show a grabbing cursor and suspend scroll-snap mid-drag.
  function setupDragScroll() {
    document.querySelectorAll('[data-drag-scroll]').forEach(el => {
      let isDown = false;
      let moved = false;
      let startX = 0;
      let startScrollLeft = 0;

      el.addEventListener('mousedown', event => {
        isDown = true;
        moved = false;
        startX = event.clientX;
        startScrollLeft = el.scrollLeft;
        // Without this, starting the drag on a child <img> (thumbnails,
        // video posters) or button hands the gesture to the browser's
        // own native image-drag/text-selection instead - it "wins" over
        // the mousemove tracking below, so the drag never registers.
        event.preventDefault();
      });

      window.addEventListener('mousemove', event => {
        if (!isDown) return;
        const dx = event.clientX - startX;
        if (Math.abs(dx) > 4) {
          moved = true;
          el.classList.add('is-dragging');
        }
        el.scrollLeft = startScrollLeft - dx;
      });

      window.addEventListener('mouseup', () => {
        if (!isDown) return;
        isDown = false;
        el.classList.remove('is-dragging');
      });

      // A real drag ending over a child button (a thumbnail, or the
      // video's own play button) would otherwise also fire a click on
      // it - swallow just that one click, a plain tap/click without
      // movement still works normally.
      el.addEventListener('click', event => {
        if (moved) {
          event.preventDefault();
          event.stopPropagation();
        }
      }, true);
    });
  }

})();
